import {useCallback, useContext, useState} from 'react'

import PropTypes from 'prop-types'

import {useDecisionCallback} from '@s-ui/pde'
import {atomIconSizes} from '@s-ui/react-atom-icon'
import Context from '@s-ui/react-context'
import {AuthenticationContext} from '@adv-mt/mt-context-authentication'
import {LoginModalContext} from '@adv-mt/mt-context-login-modal'
import IconWhatsapp from '@adv-mt/mt-svg-icons/lib/Whatsapp'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'
import {Button, BUTTON_SHAPES, BUTTON_SIZES, BUTTON_VARIANTS} from '@adv-mt/ui/button'

import {useContactSellerV2Config} from '../context/ContactSellerV2Context.js'
import {CONTACT_ACTION_IDS} from '../contracts.js'

export const WHATSAPP_ASPECTS = {
  SOLID: 'solid'
}

const BASE_CLASS = 'mt-ContactCardV2-actionWhatsapp'

const ASPECT_VARIANTS = {
  [WHATSAPP_ASPECTS.SOLID]: BUTTON_VARIANTS.SOLID
}

const SKIP_LOGIN_FLAG_KEY = 'ff_lead_experience_login_whatsapp'

const isSafariUA = userAgent => /^(?!.*(?:Chrome|CriOS|Chromium|Edg|OPR)).*Safari/i.test(userAgent || '')

const WhatsappButton = ({aspect = WHATSAPP_ASPECTS.SOLID, iconOnlyCapable}) => {
  const {adId, contractId, pageName, offerType, onContact, onWhatsappError} = useContactSellerV2Config()
  const {i18n, domain, browser} = useContext(Context)
  const {isLogged} = useContext(AuthenticationContext)
  const {triggerLoginModal} = useContext(LoginModalContext)
  const {decide} = useDecisionCallback()
  const {track} = useAnalyticsTrack()
  const [isCreatingChat, setIsCreatingChat] = useState(false)

  const config = domain.get('config')
  const {LOGIN_SOURCE_ID_WHATSAPP_LEAD} = config.get('login')
  const {
    HIT_ORIGINS: {LEAD_WHATSAPP: hitOriginLeadWhatsapp, AD_DETAIL: hitOriginAdDetail}
  } = config.get('tracking')

  const label = i18n.t('SHARE_PROVIDERS.WHATSAPP')

  const isSafari = isSafariUA(browser?.userAgent)

  const openChat = useCallback(async () => {
    const tab = isSafari ? window.open('', '_blank') : null

    if (isSafari && (!tab || tab.closed || typeof tab.closed === 'undefined')) {
      return
    }

    setIsCreatingChat(true)

    const [chatError, chatResponse] = await domain
      .get('create_whatsapp_intents_user_use_case')
      .execute({resourceId: adId})

    setIsCreatingChat(false)

    if (chatError || !chatResponse?.chatDeepLink) {
      tab?.close()
      onWhatsappError?.()
      return
    }

    onContact?.({actionId: CONTACT_ACTION_IDS.WHATSAPP, adId, contractId, pageName, offerType, id: chatResponse.id})

    if (tab) {
      tab.location.href = chatResponse.chatDeepLink
    } else {
      window.open(chatResponse.chatDeepLink, '_blank', 'noopener,noreferrer')
    }
  }, [adId, contractId, domain, isSafari, offerType, onContact, onWhatsappError, pageName])

  const handleClick = useCallback(async () => {
    await track({event: 'Whatsapp Button Clicked', data: {hit_origin: hitOriginAdDetail}})

    const {enabled: isSkipLoginEnabled} = decide(SKIP_LOGIN_FLAG_KEY)
    const isLoginRequired = !isLogged && !isSkipLoginEnabled

    if (isLoginRequired) {
      triggerLoginModal({
        modalProps: {trackingOrigin: hitOriginLeadWhatsapp, source: LOGIN_SOURCE_ID_WHATSAPP_LEAD, onLogin: openChat}
      })
      return
    }

    openChat()
  }, [
    decide,
    hitOriginAdDetail,
    hitOriginLeadWhatsapp,
    isLogged,
    LOGIN_SOURCE_ID_WHATSAPP_LEAD,
    openChat,
    track,
    triggerLoginModal
  ])

  return (
    <Button
      aria-label={i18n.t('CONTACT_SELLER_V2.ACTIONS.WHATSAPP_ARIA_LABEL')}
      className={`${BASE_CLASS} ${BASE_CLASS}--${aspect}${iconOnlyCapable ? ` ${BASE_CLASS}--iconOnly` : ''}`}
      isLoading={isCreatingChat}
      onClick={handleClick}
      shape={BUTTON_SHAPES.CIRCLE}
      size={BUTTON_SIZES.LARGE}
      variant={ASPECT_VARIANTS[aspect]}
    >
      <IconWhatsapp size={atomIconSizes.medium} />
      {!iconOnlyCapable && label}
    </Button>
  )
}

WhatsappButton.displayName = 'WhatsappButton'

WhatsappButton.propTypes = {
  aspect: PropTypes.oneOf(Object.values(WHATSAPP_ASPECTS)),
  iconOnlyCapable: PropTypes.bool
}

export default WhatsappButton
